import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon, DocumentDuplicateIcon, MusicalNoteIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { BaseTrackItemProps } from './BaseTrackItem';

interface VideoTrackItemProps extends BaseTrackItemProps {
  onInsertClip?: (afterClipId: string) => void;
  onDeleteClip?: (clipId: string) => void;
  onCopyClip?: (clipId: string) => void;
  onToggleVolume?: (clipId: string, volume: number) => void;
}

export default function VideoTrackItem({ clip, pixelsPerSecond, isSelected, onSelect, onInsertClip, onDeleteClip, onCopyClip, onToggleVolume }: VideoTrackItemProps) {
  const { t } = useTranslation('editor');
  const [isHovered, setIsHovered] = useState(false);
  const clipWidth = clip.duration * pixelsPerSecond;
  const hasImages = clip.videoClip && (clip.videoClip.startImage || clip.videoClip.endImage);
  const hasVideos = clip.videoClip && clip.videoClip.videoUrl;
  const startImage = hasImages ? clip.videoClip.startImage : null;
  const endImage = hasImages ? clip.videoClip.endImage : null;

  const getBackgroundColor = () => {
    if (clip.isGenerating) return clip.color;
    return hasImages ? '' : 'bg-purple-500';
  };

  const getBorderColor = () => {
    return isSelected ? 'border-blue-400' : 'border-black';
  };

  const handleInsertClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onInsertClip) {
      onInsertClip(clip.id);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteClip) {
      onDeleteClip(clip.id);
    }
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCopyClip) {
      onCopyClip(clip.id);
    }
  };

  const handleVolumeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentVolume = clip.editInfo?.videoVolume ?? 0;
    if (onToggleVolume) {
      const newVolume = currentVolume === 0 ? 1 : 0;
      onToggleVolume(clip.id, newVolume);
    }
  };

  const currentVolume = clip.editInfo?.videoVolume ?? 0;

  return (
    <div
      className={`absolute top-1 bottom-1 rounded-lg border-2 cursor-pointer hover:opacity-80 transition-all overflow-visible ${getBackgroundColor()
        } ${getBorderColor()} ${isSelected ? 'bg-blue-200/30 shadow-lg' : ''}`}
      style={{
        left: `${clip.startTime * pixelsPerSecond}px`,
        width: `${clipWidth}px`
      }}
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasImages ? (
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          {startImage && endImage ? (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${startImage}), url(${endImage})`,
                backgroundSize: 'auto 100%, auto 100%',
                backgroundRepeat: 'repeat-x, repeat-x',
                backgroundPosition: 'left center, right center'
              }}
            />
          ) : startImage ? (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${startImage})`,
                backgroundSize: 'auto 100%',
                backgroundRepeat: 'repeat-x',
                backgroundPosition: 'center'
              }}
            />
          ) : endImage ? (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${endImage})`,
                backgroundSize: 'auto 100%',
                backgroundRepeat: 'repeat-x',
                backgroundPosition: 'center'
              }}
            />
          ) : null}

          {clip.isGenerating && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-1 h-full flex items-center relative z-10">
          {clip.isGenerating ? (
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              <span className="text-xs text-white truncate drop-shadow-md">{t('timeline.videoTrackItem.generating')}</span>
            </div>
          ) : (
            <span className="text-xs text-white truncate drop-shadow-md">{clip.name}</span>
          )}
        </div>
      )}

      {/* 音量开关 - 左上角 */}
      <button
        onClick={handleVolumeToggle}
        className="absolute left-1 top-1 w-4 h-4 bg-black/60 rounded flex items-center justify-center z-10 cursor-pointer transition-colors hover:bg-black/80"
        title={currentVolume === 0 ? t('timeline.videoTrackItem.enableSound') : t('timeline.videoTrackItem.disableSound')}
        aria-label={currentVolume === 0 ? t('timeline.videoTrackItem.enableSound') : t('timeline.videoTrackItem.disableSound')}
      >
        {currentVolume === 0 ? (
          <BellSlashIcon className="w-3 h-3 text-red-400" />
        ) : (
          <MusicalNoteIcon className="w-3 h-3 text-green-400" />
        )}
      </button>

      {/* 悬浮 + 按钮 - 右上角 */}
      {isHovered && onInsertClip && (
        <button
          onClick={handleInsertClick}
          className="absolute right-1 top-1 w-4 h-4 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center shadow-md z-10 transition-colors"
          title={t('timeline.videoTrackItem.insertAfter')}
        >
          <PlusIcon className="w-3 h-3 text-white" />
        </button>
      )}

      {/* 悬浮复制按钮 - 左下角 */}
      {isHovered && onCopyClip && (
        <button
          onClick={handleCopyClick}
          className="absolute left-1 bottom-1 w-4 h-4 bg-black/60 rounded flex items-center justify-center z-10 transition-colors"
          title={t('timeline.videoTrackItem.copyClip')}
        >
          <DocumentDuplicateIcon className="w-3 h-3 text-blue-400 hover:text-blue-300" />
        </button>
      )}

      {/* 悬浮删除按钮 - 右下角 */}
      {isHovered && onDeleteClip && (
        <button
          onClick={handleDeleteClick}
          className="absolute right-1 bottom-1 w-4 h-4 bg-black/60 rounded flex items-center justify-center z-10 transition-colors"
          title={t('timeline.videoTrackItem.deleteClip')}
        >
          <TrashIcon className="w-3 h-3 text-red-400 hover:text-red-300" />
        </button>
      )}
    </div>
  );
}
